import './CreateMessagePopup.scss'
import CheckBox from "devextreme-react/check-box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import TagBox from "devextreme-react/tag-box";
import TextArea from "devextreme-react/text-area";
import TextBox from "devextreme-react/text-box";
import ValidationSummary from "devextreme-react/validation-summary";
import Validator, { EmailRule, RequiredRule, StringLengthRule } from "devextreme-react/validator";
import { useEffect, useMemo, useRef, useState } from "react";
import { Field } from "../../../../layouts";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useCreateMessage } from "../api/create-message";
import { LoadPopupPanel } from '../../../../components';
import { CreateMessagePopupProps } from '../types/CreateMessagePopupProps';
import { useGetMessageRecipients } from '../api/get-message-recipients';
import { NewMessage } from '../types/NewMessage';
import { MessageType } from '../types/MessageType';
import { EmailRecipient } from '../types/EmailRecipient';
import RadioGroup from 'devextreme-react/radio-group';
import SelectBox from 'devextreme-react/select-box';
import { SmsRecipient } from '../types/SmsRecipient';
import { useGetMessage } from '../api/get-message';
import { formatPhoneNumber } from '../../../../utils/string-utils';

export default function CreateMessagePopup(props: CreateMessagePopupProps) {

	const message = useGetMessage(props.replyToMessageID);

	const MAX_SMS_LENGTH = 160;
	const [messageText, setMessageText] = useState(props.initialMessageText);
	const [messageType, setMessageType] = useState(message.data?.messageTypeID ?? MessageType.Email);
	const [emailsTo, setEmailsTo] = useState<string[]>(
		!!message.data && message.data.messageTypeID === MessageType.Email ? [message.data.fromAddress] : []);

	const [phoneNumber, setPhoneNumber] = useState(
		!!message.data && message.data.messageTypeID === MessageType.SMS ? formatPhoneNumber(message.data.fromAddress) : undefined);
	const [urgent, setUrgent] = useState(false);
	const [createNote, setCreateNote] = useState(true);
	const [emailSubject, setEmailSubject] = useState(message.data?.subject ?? props.emailSubject);

	const recipients = useGetMessageRecipients(props.orderID, props.kindOfOrder);
	const customerEmail = recipients.data?.emailRecipients.find(x => x.role === 'Customer')?.emailAddress;
	const customerPhone = recipients.data?.smsRecipients.find(x => x.role === 'Customer')?.phone;

	const messageTypes = useMemo(() => {
		return [
			{
				value: MessageType.Email,
				text: 'Email'
			},
			{
				value: MessageType.SMS,
				text: 'SMS'
			}
		];
	}, []);

	const createMessage = useCreateMessage(
		() => {
			props.onHidePopup();
			showSuccessMessage("The message has been created");
			props.onMessageCreated();
		},
		(error) => showErrorMessage(error)
	);

	return (
		<>
			<Popup
				title="Create Message"
				visible={true}
				hideOnOutsideClick={true}
				onHiding={() => {
					props.onHidePopup();
				}}
				width={700}
				height={600}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: () => {
							props.onHidePopup();
						}
					}}
					widget="dxButton"
					location="after"
				/>

				<ValidationSummary validationGroup='createMessageValidationGroup'></ValidationSummary>
				<div className='create-message-popup-content'>
					{
						!props.disableSMS &&
						<Field label="Message Type">
							<RadioGroup
								layout='horizontal'
								dataSource={messageTypes}
								valueExpr={'value'}
								displayExpr={'text'}
								value={messageType}
								onValueChanged={(e) => {
									setMessageType(e.value);
								}}
							/>
						</Field>
					}
					<Field label="Subject" visible={messageType === MessageType.Email}>
						<TextBox value={emailSubject} onValueChanged={(e) => setEmailSubject(e.value)}>
							<Validator validationGroup="createMessageValidationGroup">
								<RequiredRule message={'Subject is required'} />
							</Validator>
						</TextBox>
					</Field>

					<Field label="To" visible={messageType === MessageType.Email}>
						<TagBox
							value={emailsTo}
							onValueChanged={(e) => setEmailsTo(e.value)}
							dataSource={recipients.data?.emailRecipients}
							searchEnabled={true}
							applyValueMode={'instantly'}
							acceptCustomValue={true}
							valueExpr='emailAddress'
							displayExpr='emailAddress'
							onCustomItemCreating={(e) => {
								if (e.text) {
									e.customItem = {
										emailAddress: e.text,
										fullName: '',
										role: ''
									} as EmailRecipient;
									setEmailsTo(emailsTo.concat([e.text!]));
								}
							}}
							onSelectionChanged={(e) => {
								if (e.addedItems.length > 0) {
									setTimeout(() => { e.component.open(); }, 0);
								}
							}}
							itemRender={(itemData: EmailRecipient) => {
								return (<div className={'recipient-item' + (itemData.role === "Customer" ? ' customer' : '')}>
									<div className='recipient-item-email'>{itemData.emailAddress}</div>
									<div className='recipient-item-name'>{itemData.fullName}</div>
									<div className='recipient-item-role'>{itemData.role}</div>
								</div>);
							}}
							tagRender={(itemData: EmailRecipient) => {
								return (<div className={'dx-tag-content' + (itemData.role === 'Customer' ? ' customer' : '')}>
									<span>{itemData.emailAddress}</span>
									<div className='dx-tag-remove-button'></div>
								</div>);
							}}
						>
							<Validator validationGroup="createMessageValidationGroup">
								<RequiredRule message={'To is required'} />
								<EmailRule message={'Invalid email address'} />
							</Validator>
						</TagBox>
						{
							!!customerEmail && emailsTo.includes(customerEmail) &&
							<div className='customer-message-warning'>
								*Outgoing message to customer
							</div>
						}
					</Field>
					<Field label='To' visible={messageType === MessageType.SMS}>
						<SelectBox
							className={phoneNumber === customerPhone ? 'customer' : ''}
							value={phoneNumber}
							onValueChanged={(e) => setPhoneNumber(e.value)}
							dataSource={recipients.data?.smsRecipients}
							valueExpr='phone'
							displayExpr='phone'
							itemRender={(itemData: SmsRecipient) => {
								return (<div className={'recipient-item' + (itemData.role === "Customer" ? ' customer' : '')}>
									<div className='recipient-item-phone'>{itemData.phone}</div>
									<div className='recipient-item-name'>{itemData.fullName}</div>
									<div className='recipient-item-role'>{itemData.role}</div>
								</div>);
							}}
						>
							<Validator validationGroup="createMessageValidationGroup">
								<RequiredRule message={'To is required'} />
							</Validator>
						</SelectBox>
						{
							!!customerPhone && phoneNumber === customerPhone &&
							<div className='customer-message-warning'>
								*Outgoing message to customer
							</div>
						}
					</Field>

					<Field label="Urgent" visible={messageType === MessageType.Email}>
						<CheckBox value={urgent} onValueChanged={(e) => setUrgent(e.value)} />
					</Field>
					<Field label="Create Note" visible={messageType === MessageType.Email}>
						<CheckBox value={createNote} onValueChanged={(e) => setCreateNote(e.value)} />
					</Field>
					<TextArea
						value={messageText}
						height={180}
						valueChangeEvent='keyup'
						onValueChanged={(e) => setMessageText(e.value)}
					>
						<Validator validationGroup="createMessageValidationGroup">
							<RequiredRule message={'Message is required'} />
							{
								messageType === MessageType.SMS &&
								<StringLengthRule max={MAX_SMS_LENGTH}
									message={`SMS message cannot be longer than ${MAX_SMS_LENGTH} characters`} />
							}
						</Validator>
					</TextArea>
				</div>

				<ToolbarItem
					options={{
						disabled: createMessage.isLoading,
						validationGroup: 'createMessageValidationGroup',
						type: 'default',
						stylingMode: 'contained',
						text: 'Send Message',
						onClick: (e: any) => {
							let validation = e.validationGroup.validate();
							if (validation.isValid) {
								const newMessage: NewMessage = {
									orderID: props.orderID,
									kindOfOrder: props.kindOfOrder,
									messageType: messageType,
									body: messageText!,
									subject: emailSubject,
									emailAddress: emailsTo.join("; "),
									phoneNumber: phoneNumber,
									isUrgent: urgent,
									createNote: createNote
								};

								createMessage.mutate(newMessage);
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						onClick: () => {
							props.onHidePopup();
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				/>
				<LoadPopupPanel visible={createMessage.isLoading || recipients.isLoading} />
			</Popup>
		</>
	)
}
